import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { flows } from '@repletoryreach/sdk-api';
import { Stack, Card, Group, Button, Text, Title, Badge } from '@repletoryreach/sdk-ui';
import { Alert, EmptyState } from '@repletoryreach/sdk-ui/data-display';
import { openConfirmModal } from '@repletoryreach/sdk-ui/modals';
import { NumberInput, Select, Textarea, TextInput } from '@repletoryreach/sdk-ui/inputs';
import { notifySuccess, notifyError } from '@repletoryreach/sdk-ui/notifications';
import { useT } from '../../../i18n/I18nProvider';
import { formatCount, formatDateTime, formatRelative } from '../../../lib/format';
import { formatCredits } from '../../../lib/credits';
import { openKbDocument } from '../../knowledge/open-document';
import {
  MAX_KEYWORDS, MAX_KEYWORD_LENGTH, VERDICT_COLOUR, countsSentence, keywordsFit, parseKeywords, proposalList, proposalText, verdictsPresent
} from './deep-review';

const MUTED = 'var(--rr-color-text-muted)';
const COACH_KEY = ['bot-coach'];
const errorText = (e: unknown, fallback: string) => {
  const body = (e as { body?: { error?: { message?: string } } } | null)?.body?.error?.message;
  return body ?? (e instanceof Error ? e.message : fallback);
};
const busy = (r?: flows.BotCoachRun | null) => r?.status === 'queued' || r?.status === 'running';

/**
 * The bot coach (owner's decisions of 8 October 2026): it reads the chatbot's recent conversations and suggests
 * fixes; nothing changes until someone presses Apply. Review now is priced first; a schedule runs it daily or
 * weekly at the business's own time.
 *
 * The deep review (29 October 2026) judges every chatbot reply against the knowledge base and the routing: a wrong
 * answer, an answer to something not asked, a flow started by the wrong keyword. Its findings carry a verdict and
 * the exchange, and it keeps a schedule of its own on the coach's day and time.
 */
export function CoachPanel() {
  const t = useT();
  const qc = useQueryClient();
  const overview = useQuery({ queryKey: COACH_KEY, queryFn: () => flows.getBotCoach(), refetchInterval: (q) => (busy(q.state.data?.data.latest_run) ? 3000 : false) });
  const suggestions = useQuery({ queryKey: [...COACH_KEY, 'suggestions'], queryFn: () => flows.listBotCoachSuggestions({ status: 'open' }) });
  const latest = overview.data?.data.latest_run;
  const wasBusy = useWasBusy(busy(latest));
  useEffect(() => { if (wasBusy && !busy(latest)) void qc.invalidateQueries({ queryKey: [...COACH_KEY, 'suggestions'] }); }, [wasBusy, latest, qc]);

  const start = useMutation({
    mutationFn: (mode: flows.BotCoachMode) => flows.startBotCoachRun(mode),
    onSuccess: (_r, mode) => {
      void qc.invalidateQueries({ queryKey: COACH_KEY });
      notifySuccess(t(mode === 'deep' ? 'flows.coach.startedDeep' : 'flows.coach.started'), t('flows.coach.title'));
    },
    onError: (e) => notifyError(errorText(e, t('common.unknownError')), t('flows.coach.title'))
  });
  const [pricing, setPricing] = useState<flows.BotCoachMode | null>(null);

  async function review(mode: flows.BotCoachMode) {
    setPricing(mode);
    try {
      const e = (await flows.estimateBotCoach(mode)).data;
      if (e.conversations === 0) { notifySuccess(t('flows.coach.nothing'), t('flows.coach.title')); return; }
      const deep = mode === 'deep';
      openConfirmModal({
        title: t(deep ? 'flows.coach.deepConfirmTitle' : 'flows.coach.confirmTitle'),
        children: (
          <Stack gap="xs">
            {deep && <Text role="bodySmall" c={MUTED}>{t('flows.coach.deepAbout')}</Text>}
            <Text role="bodySmall">{t('flows.coach.confirmBody', { count: formatCount(e.conversations), since: formatDateTime(e.since) })}</Text>
            {deep && (
              <Text role="bodySmall" data-testid="coach-deep-judge">{t(e.judge_fallback ? 'flows.coach.deepJudgeFallback' : 'flows.coach.deepJudge')}</Text>
            )}
            <Text role="bodySmall" fw={650} data-testid={deep ? 'coach-deep-estimate' : 'coach-estimate'}>
              {e.priced ? t('flows.coach.cost', { amount: formatCredits(e.estimated_minor, t, e.currency) }) : t('flows.coach.costUnknown')}
            </Text>
            {e.priced && !e.affordable && <Alert color="warning">{t('flows.coach.notEnough')}</Alert>}
          </Stack>
        ),
        labels: { confirm: t(deep ? 'flows.coach.runDeep' : 'flows.coach.run'), cancel: t('action.cancel') },
        confirmProps: { disabled: e.priced && !e.affordable },
        onConfirm: () => start.mutate(mode)
      });
    } catch (err) {
      notifyError(errorText(err, t('common.unknownError')), t('flows.coach.title'));
    } finally {
      setPricing(null);
    }
  }

  const rows = suggestions.data?.data ?? [];
  // The filter by verdict: All, then each verdict the deep review found, with how many.
  const [verdict, setVerdict] = useState<flows.ReviewVerdict | 'all'>('all');
  const present = verdictsPresent(rows);
  const shown = verdict === 'all' || !present.some((p) => p.verdict === verdict) ? rows : rows.filter((s) => s.verdict === verdict);
  const active = shown === rows ? 'all' : verdict;
  return (
    <Stack gap="md" data-guide="flows-coach">
      <Card withBorder>
        <Stack gap="sm">
          <Group justify="space-between" align="flex-start" wrap="wrap">
            <Stack gap={2} style={{ flex: '1 1 320px' }}>
              <Title order={4}>{t('flows.coach.title')}</Title>
              <Text role="bodySmall" c={MUTED}>{t('flows.coach.description')}</Text>
            </Stack>
            <Group gap="xs" wrap="wrap">
              <Button onClick={() => void review('quick')} loading={pricing === 'quick' || (start.isPending && start.variables === 'quick')} disabled={busy(latest) || pricing === 'deep'} data-testid="coach-review">
                {busy(latest) ? t('flows.coach.reviewing') : t('flows.coach.reviewNow')}
              </Button>
              <Button variant="default" onClick={() => void review('deep')} loading={pricing === 'deep' || (start.isPending && start.variables === 'deep')} disabled={busy(latest) || pricing === 'quick'} data-testid="coach-deep-review">
                {t('flows.coach.deepReview')}
              </Button>
            </Group>
          </Group>
          {latest && <LatestRun run={latest} />}
        </Stack>
      </Card>

      {overview.data && <ScheduleCard settings={overview.data.data.settings} />}

      <Stack gap="sm">
        <Title order={5}>{t('flows.coach.suggestions')}</Title>
        {present.length > 0 && (
          <Group gap={6} wrap="wrap" role="group" aria-label={t('flows.coach.filter')} data-testid="coach-filter">
            <Button size="xs" variant={active === 'all' ? 'filled' : 'default'} aria-pressed={active === 'all'} onClick={() => setVerdict('all')}>
              {t('flows.coach.filter.all', { count: formatCount(rows.length) })}
            </Button>
            {present.map((p) => (
              <Button key={p.verdict} size="xs" variant={active === p.verdict ? 'filled' : 'default'} aria-pressed={active === p.verdict}
                onClick={() => setVerdict(p.verdict)} data-testid={`coach-filter-${p.verdict}`}>
                {t('flows.coach.filter.count', { label: t(`flows.coach.verdict.${p.verdict}`), count: formatCount(p.count) })}
              </Button>
            ))}
          </Group>
        )}
        {suggestions.error && <Alert color="error" role="alert">{errorText(suggestions.error, t('common.unknownError'))}</Alert>}
        {!suggestions.isPending && rows.length === 0 && (
          <EmptyState title={t('flows.coach.noSuggestions')} description={t('flows.coach.noSuggestionsHint')} />
        )}
        {shown.map((s) => <SuggestionCard key={s.id} s={s} />)}
      </Stack>
    </Stack>
  );
}

/** True for one render after the value was true, so a finished review can refresh the list once. */
function useWasBusy(now: boolean) {
  const [was, setWas] = useState(now);
  useEffect(() => { setWas(now); }, [now]);
  return was;
}

function LatestRun({ run }: { run: flows.BotCoachRun }) {
  const t = useT();
  const tone = run.status === 'done' ? undefined : run.status === 'failed' ? 'error' : 'info';
  const deep = run.mode === 'deep';
  const counted = deep && run.status === 'done' ? countsSentence(run.counts, run.conversations, t) : null;
  return (
    <Stack gap={4} data-testid="coach-latest">
      <Group gap="xs" wrap="wrap" align="center">
        <Badge size="xs" variant="light" color={deep ? 'violet' : 'gray'} data-testid="coach-latest-mode">{t(deep ? 'flows.coach.mode.deep' : 'flows.coach.mode.quick')}</Badge>
        <Text role="caption" c={MUTED}>
          {t(`flows.coach.status.${run.status}`)} · {formatRelative(run.finished_at ?? run.created_at)}
          {run.status === 'done' && ` · ${t('flows.coach.found', { conversations: formatCount(run.conversations), suggestions: formatCount(run.suggestions_found) })}`}
          {run.automatic && ` · ${t('flows.coach.scheduled')}`}
        </Text>
      </Group>
      {counted && <Text role="bodySmall" data-testid="coach-counts">{counted}</Text>}
      {run.summary && <Text role="bodySmall">{run.summary}</Text>}
      {run.error && tone && <Alert color={tone === 'error' ? 'error' : 'info'}>{run.error}</Alert>}
    </Stack>
  );
}

function ScheduleCard({ settings }: { settings: flows.BotCoachSettings }) {
  const t = useT();
  const qc = useQueryClient();
  const [schedule, setSchedule] = useState<flows.BotCoachSchedule>(settings.schedule);
  const [weekday, setWeekday] = useState(String(settings.weekday));
  const [time, setTime] = useState(settings.local_time);
  const [max, setMax] = useState<number>(settings.max_conversations);
  const [deepSchedule, setDeepSchedule] = useState<flows.BotCoachSchedule>(settings.deep_schedule ?? 'off');
  const [deepMax, setDeepMax] = useState<number>(settings.deep_max_conversations ?? 25);
  const save = useMutation({
    mutationFn: () => flows.saveBotCoachSettings({
      schedule, weekday: Number(weekday), local_time: time, max_conversations: max,
      deep_schedule: deepSchedule, deep_max_conversations: deepMax
    }),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: COACH_KEY }); notifySuccess(t('flows.coach.saved'), t('flows.coach.scheduleTitle')); },
    onError: (e) => notifyError(errorText(e, t('common.unknownError')), t('flows.coach.scheduleTitle'))
  });
  const days = [0, 1, 2, 3, 4, 5, 6].map((d) => ({ value: String(d), label: t(`flows.coach.day.${d}`) }));
  const choices = (['off', 'daily', 'weekly'] as const).map((v) => ({ value: v, label: t(`flows.coach.schedule.${v}`) }));
  // The deep review runs on the coach's day and time, so those show when either review is on a schedule.
  const weekly = schedule === 'weekly' || deepSchedule === 'weekly';
  const timed = schedule !== 'off' || deepSchedule !== 'off';
  return (
    <Card withBorder data-guide="coach-schedule">
      <form onSubmit={(e) => { e.preventDefault(); save.mutate(); }} noValidate>
        <Stack gap="sm">
          <Title order={5}>{t('flows.coach.scheduleTitle')}</Title>
          <Group gap="sm" align="flex-start" wrap="wrap">
            <Select label={t('flows.coach.schedule')} value={schedule} onChange={(e) => setSchedule(e.currentTarget.value as flows.BotCoachSchedule)} data={choices} />
            <NumberInput label={t('flows.coach.max')} description={t('flows.coach.maxHint')} min={5} max={100} value={max}
              onChange={(v) => setMax(typeof v === 'number' ? v : 40)} />
          </Group>
          <Group gap="sm" align="flex-start" wrap="wrap" data-testid="coach-deep-schedule">
            <Select label={t('flows.coach.deepSchedule')} description={t('flows.coach.deepScheduleHint')} value={deepSchedule}
              onChange={(e) => setDeepSchedule(e.currentTarget.value as flows.BotCoachSchedule)} data={choices} data-testid="coach-deep-schedule-select" />
            <NumberInput label={t('flows.coach.deepMax')} description={t('flows.coach.deepMaxHint')} min={1} max={100} value={deepMax}
              onChange={(v) => setDeepMax(typeof v === 'number' ? v : 25)} data-testid="coach-deep-max" />
          </Group>
          {timed && (
            <Group gap="sm" align="flex-start" wrap="wrap">
              {weekly && <Select label={t('flows.coach.weekday')} value={weekday} onChange={(e) => setWeekday(e.currentTarget.value)} data={days} />}
              <TextInput label={t('flows.coach.time')} type="time" value={time} onChange={(e) => setTime(e.currentTarget.value)} />
            </Group>
          )}
          <Text role="caption" c={MUTED}>
            {schedule === 'off' ? t('flows.coach.offHint') : t('flows.coach.scheduleHint')}
            {settings.next_run_at && schedule !== 'off' && ` ${t('flows.coach.next', { when: formatDateTime(settings.next_run_at) })}`}
            {settings.deep_next_run_at && deepSchedule !== 'off' && ` ${t('flows.coach.deepNext', { when: formatDateTime(settings.deep_next_run_at) })}`}
          </Text>
          <Group justify="flex-end">
            <Button type="submit" size="sm" loading={save.isPending}>{t('action.saveChanges')}</Button>
          </Group>
        </Stack>
      </form>
    </Card>
  );
}

/** A deep finding's exchange: what the customer said, what the chatbot said, what is right and where that is written. */
function Exchange({ s }: { s: flows.BotCoachSuggestion }) {
  const t = useT();
  const sources = (s.sources ?? []).map((x) => x.name).filter(Boolean);
  if (!s.customer_said && !s.bot_said && !s.right_answer) return null;
  return (
    <div style={{ borderLeft: '3px solid var(--rr-color-border)', paddingLeft: 10, display: 'grid', gap: 6, minWidth: 0, overflowWrap: 'anywhere' }} data-testid="coach-exchange">
      {s.customer_said && <Line label={t('flows.coach.customerSaid')} text={s.customer_said} />}
      {s.bot_said && <Line label={t('flows.coach.botSaid')} text={s.bot_said} />}
      {s.right_answer && <Line label={t('flows.coach.rightAnswer')} text={s.right_answer} />}
      {sources.length > 0 && <Text role="caption" c={MUTED}>{t('flows.coach.sources', { names: sources.join(', ') })}</Text>}
    </div>
  );
}

function Line({ label, text }: { label: string; text: string }) {
  return (
    <Stack gap={0}>
      <Text role="caption" fw={650}>{label}</Text>
      <Text role="caption" style={{ whiteSpace: 'pre-wrap' }}>{text}</Text>
    </Stack>
  );
}

function SuggestionCard({ s }: { s: flows.BotCoachSuggestion }) {
  const t = useT();
  const qc = useQueryClient();
  const [draft, setDraft] = useState<flows.BotCoachSuggestion['proposal']>(s.proposal ?? {});
  const set = (k: string, v: string) => setDraft((d) => ({ ...d, [k]: v }));
  // The keywords fix: the new keywords as typed, with commas, sent as a list.
  const [keywords, setKeywords] = useState(proposalList(s.proposal?.after).join(', '));
  const parsed = parseKeywords(keywords);
  const keywordsOk = keywordsFit(parsed);
  const [opening, setOpening] = useState(false);
  const done = () => { void qc.invalidateQueries({ queryKey: COACH_KEY }); };
  const payload = (): Record<string, string | string[]> | undefined =>
    s.kind === 'note' || s.kind === 'document' ? undefined : s.kind === 'keywords' ? { after: parsed } : draft;
  const apply = useMutation({
    mutationFn: () => flows.applyBotCoachSuggestion(s.id, payload()),
    onSuccess: () => { done(); notifySuccess(t('flows.coach.applied'), s.title); },
    onError: (e) => notifyError(errorText(e, t('common.unknownError')), s.title)
  });
  const dismiss = useMutation({
    mutationFn: () => flows.dismissBotCoachSuggestion(s.id),
    onSuccess: () => { done(); notifySuccess(t('flows.coach.dismissed'), s.title); },
    onError: (e) => notifyError(errorText(e, t('common.unknownError')), s.title)
  });
  async function openDocument() {
    const id = proposalText(draft.document_id);
    if (!id) return;
    setOpening(true);
    try {
      await openKbDocument({ id, name: proposalText(draft.document_name) });
    } catch (e) {
      notifyError(errorText(e, t('common.unknownError')), s.title);
    } finally {
      setOpening(false);
    }
  }
  // A deep finding's lines repeat what the customer and the chatbot said, which the exchange already shows.
  const lines = s.verdict ? [] : s.evidence?.lines ?? [];
  const flowId = proposalText(draft.flow_id);
  const field = proposalText(draft.field);
  const markOnly = s.kind === 'note' || s.kind === 'document';
  return (
    <Card withBorder data-testid="coach-suggestion" data-verdict={s.verdict ?? undefined}>
      <Stack gap="sm">
        <Group justify="space-between" align="flex-start" wrap="nowrap">
          <Stack gap={2} style={{ minWidth: 0 }}>
            <Group gap="xs" wrap="wrap">
              {s.verdict && (
                <Badge size="xs" variant="light" color={VERDICT_COLOUR[s.verdict]} data-testid="coach-verdict">{t(`flows.coach.verdict.${s.verdict}`)}</Badge>
              )}
              <Badge size="xs" variant="light">{t(`flows.coach.kind.${s.kind}`)}</Badge>
              <Text role="bodySmall" fw={700}>{s.title}</Text>
            </Group>
            <Text role="bodySmall" c={MUTED}>{s.problem}</Text>
          </Stack>
        </Group>
        {s.verdict && <Exchange s={s} />}
        {lines.length > 0 && (
          <div style={{ borderLeft: "3px solid var(--rr-color-border)", paddingLeft: 10, display: "grid", gap: 2 }}>
            {lines.map((l, i) => <Text key={i} role="caption">{l}</Text>)}
            {s.evidence?.conversation_id && (
              <Link to={`/inbox?conversation=${encodeURIComponent(s.evidence.conversation_id)}`}><Text role="caption">{t('flows.coach.openConversation')}</Text></Link>
            )}
          </div>
        )}
        {s.verdict && s.evidence?.conversation_id && (
          <Link to={`/inbox?conversation=${encodeURIComponent(s.evidence.conversation_id)}`}><Text role="caption">{t('flows.coach.openConversation')}</Text></Link>
        )}
        {s.kind === 'correction' && (
          <Stack gap="xs">
            <TextInput label={t('flows.coach.correctionTitle')} value={proposalText(draft.title)} onChange={(e) => set('title', e.currentTarget.value)} />
            <Textarea label={t('flows.coach.correctionAnswer')} description={t('flows.coach.correctionHint')} minRows={2} value={proposalText(draft.answer)}
              onChange={(e) => set('answer', e.currentTarget.value)} />
          </Stack>
        )}
        {s.kind === 'flow_wording' && (
          <Stack gap="xs">
            <Text role="caption" c={MUTED}>{t('flows.coach.before')}: {proposalText(draft.before)}</Text>
            <Textarea label={t('flows.coach.after')} minRows={2} value={proposalText(draft.after)} onChange={(e) => set('after', e.currentTarget.value)} />
            {flowId && <Link to={`/flows/${encodeURIComponent(flowId)}/builder`}><Text role="caption">{t('flows.coach.openFlow')}</Text></Link>}
          </Stack>
        )}
        {s.kind === 'persona' && (
          <Stack gap="xs">
            <Text role="caption" c={MUTED}>{t(`flows.coach.persona.${field || 'business_description'}`)}: {proposalText(draft.before) || t('flows.coach.empty')}</Text>
            {field === 'tone' ? (
              <Select label={t('flows.coach.after')} value={proposalText(draft.after) || 'friendly'} onChange={(e) => set('after', e.currentTarget.value)}
                data={['friendly', 'formal', 'playful'].map((v) => ({ value: v, label: t(`flows.coach.tone.${v}`) }))} />
            ) : (
              <Textarea label={t('flows.coach.after')} minRows={2} value={proposalText(draft.after)} onChange={(e) => set('after', e.currentTarget.value)} />
            )}
          </Stack>
        )}
        {s.kind === 'keywords' && (
          <Stack gap="xs" data-testid="coach-keywords">
            <Text role="caption" fw={650}>{t('flows.coach.keywords.flow', { name: proposalText(draft.flow_name) })}</Text>
            <Text role="caption" c={MUTED} data-testid="coach-keywords-now">{t('flows.coach.keywords.now', { keywords: proposalList(draft.before).join(', ') || t('flows.coach.empty') })}</Text>
            <TextInput label={t('flows.coach.keywords.after')} description={t('flows.coach.keywords.hint', { max: MAX_KEYWORDS })} value={keywords}
              error={parsed.length > 0 && !keywordsOk ? t('flows.coach.keywords.tooMany', { max: MAX_KEYWORDS, length: MAX_KEYWORD_LENGTH }) : undefined}
              onChange={(e) => setKeywords(e.currentTarget.value)} data-testid="coach-keywords-after" />
            {flowId && <Link to={`/flows/${encodeURIComponent(flowId)}/builder`}><Text role="caption">{t('flows.coach.openFlow')}</Text></Link>}
          </Stack>
        )}
        {s.kind === 'document' && (
          <Stack gap="xs" data-testid="coach-document">
            <Text role="caption" fw={650}>{t('flows.coach.document.name', { name: proposalText(draft.document_name) })}</Text>
            <Text role="caption" c={MUTED}>{t('flows.coach.document.hint')}</Text>
          </Stack>
        )}
        <Group justify="flex-end" gap="xs" wrap="wrap">
          <Button size="xs" variant="subtle" loading={dismiss.isPending} onClick={() => dismiss.mutate()}>{t('flows.coach.dismiss')}</Button>
          {s.kind === 'document' && proposalText(draft.document_id) && (
            <Button size="xs" variant="default" loading={opening} onClick={() => void openDocument()} data-testid="coach-open-document">{t('flows.coach.openDocument')}</Button>
          )}
          <Button size="xs" loading={apply.isPending} disabled={s.kind === 'keywords' && !keywordsOk} onClick={() => apply.mutate()} data-testid="coach-apply">
            {markOnly ? t('flows.coach.markDone') : t('flows.coach.apply')}
          </Button>
        </Group>
      </Stack>
    </Card>
  );
}
